/***
 *
 *   PUBLIC PROJECT INTAKE
 *   Phase 13 — reference project.$name + PublicTicketForm. Dedicated public
 *   form (not Gravity TicketForm). Skeleton loading, not-found Card, success
 *   EmptyState. Keeps /api/projectByName + /api/ticket.
 *
 *   White label: the palette and the header lockup are the surrounding
 *   PublicLayout's job. This view shares its fetch through useProjectBrand, so
 *   the two agree on the project and only one request goes out.
 *
 **********/

import React, { useContext, useState } from 'react';
import {
    Button,
    Card,
    Link,
    Skeleton,
    EmptyState,
    ProjectIdentityMark,
    PublicTicketForm,
} from 'components/lib';
import { useParams } from 'react-router-dom';
import { useProjectBrand } from 'components/hooks/branding';
import { usePortalIdentity } from 'components/hooks/portal';
import Style from './project.tailwind';
import { rootPath } from 'utilities/routes';
import { useTranslate } from 'app/translations';
import { AuthContext } from 'app/auth';
import { useApiContext } from 'app/api';

export const AccountProjectPage = () => {
    const { name } = useParams();
    const { _t } = useTranslate();
    const { user, signin } = useContext(AuthContext);
    const { callApi } = useApiContext();
    const [showForm, setShowForm] = useState(true);
    const [dashboardError, setDashboardError] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(false);

    const { project, isLoading, notFound } = useProjectBrand(name);
    const portal = usePortalIdentity(name);
    const ticketCategories = project?.ticketCategories;

    async function openDashboard() {
        if (user) return;
        if (!portal.params?.sig) return;

        setDashboardLoading(true);
        setDashboardError(null);
        try {
            const res = await callApi({
                method: 'POST',
                url: `/api/auth/project/${encodeURIComponent(name)}/portal/exchange`,
                data: {
                    email: portal.identity?.email,
                    first_name: portal.identity?.first_name,
                    last_name: portal.identity?.last_name,
                    exp: portal.params.exp,
                    sig: portal.params.sig,
                },
                auth: false,
            });

            if (res.status !== 200) {
                if (project.privacy === 'private') {
                    window.location = `/project/${name}/magic`;
                    return;
                }
                setDashboardError(_t('publicIntake.form.exchangeFailed'));
                return;
            }

            if (res.data?.['2fa_required']) {
                window.location = `/signin/otp?token=${res.data.token}`;
                return;
            }

            signin(res, res.data?.account_id, '/app/tickets');
        } finally {
            setDashboardLoading(false);
        }
    }

    if (isLoading) {
        return (
            <div className={Style.page}>
                <div className="space-y-4">
                    <Skeleton className="h-12 w-2/3" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className={Style.notFound}>
                <Card
                    variant="surface"
                    title={_t('publicIntake.notFound.title')}
                    description={_t('publicIntake.notFound.description')}
                    className="w-full"
                    last>
                    <Button
                        variant="secondary"
                        text={_t('publicIntake.notFound.backHome')}
                        url={rootPath('/')}
                    />
                </Card>
            </div>
        );
    }

    return (
        <div className={Style.page}>
            <Card variant="elevated" className={Style.card} last>
                <div className={Style.header}>
                    <div className={Style.identity}>
                        <ProjectIdentityMark
                            name={project.name}
                            filePath={project.file_path}
                            alt={_t('publicIntake.form.projectLogoAlt', {
                                project: project.name,
                            })}
                        />
                        <div className={Style.identityCopy}>
                            <p className={Style.eyebrow}>
                                {_t('publicIntake.form.projectLabel')}
                            </p>
                            <h1 className={Style.title}>{project.name}</h1>
                            <p className={Style.subtitle}>
                                {_t('publicIntake.form.projectSubtitle')}
                            </p>
                        </div>
                    </div>

                    <div className={Style.headerActions}>
                        {user ? (
                            <Link
                                url="/app/tickets"
                                text={_t('publicIntake.form.openDashboard')}
                                className={Style.dashLink}
                            />
                        ) : portal.identity ? (
                            <Button
                                text={
                                    dashboardLoading
                                        ? _t('publicIntake.form.openingDashboard')
                                        : _t('publicIntake.form.openDashboard')
                                }
                                variant="secondary"
                                size="sm"
                                isLoading={dashboardLoading}
                                action={openDashboard}
                            />
                        ) : project.privacy === 'private' ? (
                            <Button
                                text={_t('dashboard.goto')}
                                variant="secondary"
                                size="sm"
                                url={`/project/${name}/magic`}
                            />
                        ) : null}
                        {dashboardError && (
                            <p className={Style.dashError}>{dashboardError}</p>
                        )}
                    </div>
                </div>

                {showForm ? (
                    portal.isLoading ? (
                        <div className="space-y-4">
                            <Skeleton className="h-8 w-1/3" />
                            <Skeleton className="h-48 w-full" />
                        </div>
                    ) : (
                        <PublicTicketForm
                            categories={ticketCategories}
                            lockedContact={portal.identity}
                            portalParams={portal.params}
                            callback={() => setShowForm(false)}
                        />
                    )
                ) : (
                    <EmptyState
                        bare
                        icon="check-circle"
                        title={_t('request.success')}
                        buttonText={_t('request.makeAnother')}
                        action={() => setShowForm(true)}
                    />
                )}
            </Card>
        </div>
    );
};
